import { Interval } from 'luxon';
import queryString from 'query-string';

import { PaginatedData, RestaurantApiCursorPaginationParams } from 'apis/restaurant-api/types';

import {
  AggregatedPurchasedItemOptionResponse,
  AggregatedPurchasedItemOptionValue,
  AnalyticsFilter,
  AnalyticsCSVFilter,
  AnalyticsPaginatedData,
  PurchaseItemsResponse,
  PurchaseItem,
  PurchaseOrder,
  PurchasesTotals,
  Purchase,
} from './types';
import merchantGatewayApi from '../api';

export const fetchPurchases = ({
  venueId,
  startDate,
  endDate,
  limit,
  cursor,
  filters,
}: RestaurantApiCursorPaginationParams<{
  venueId: string;
  startDate: string;
  endDate: string;
  filters?: AnalyticsFilter[];
}>): Promise<PaginatedData<{ purchases: PurchaseOrder[] }>> =>
  merchantGatewayApi
    .get<AnalyticsPaginatedData<{ purchases: PurchaseOrder[] }>>(
      `/merchant-analytics-api/v2/venues/${venueId}/purchases`,
      {
        params: {
          cursor,
          start_date: startDate,
          end_date: endDate,
          limit,
          filters,
        },
        paramsSerializer: params => queryString.stringify(params, { arrayFormat: 'comma' }),
      },
    )
    .then(response => ({
      ...response.data,
      // Flatten the metadata to be able to use existing helpers for pagination
      metadata: response.data.metadata?.pagination,
    }));

export const fetchPurchasesTotals = ({
  venueId,
  startDate,
  endDate,
  filters,
}: {
  venueId: string;
  startDate: string;
  endDate: string;
  filters?: AnalyticsFilter[];
}) =>
  merchantGatewayApi
    .get<{ data: { totals: PurchasesTotals } }>(`/merchant-analytics-api/v2/venues/${venueId}/purchases/totals`, {
      params: {
        start_date: startDate,
        end_date: endDate,
        filters,
      },
      paramsSerializer: params => queryString.stringify(params, { arrayFormat: 'comma' }),
    })
    .then(response => response.data.data.totals);

export const fetchPurchase = ({ venueId, purchaseId }: { venueId: string; purchaseId: string }) =>
  merchantGatewayApi
    .get<Purchase>(`/merchant-analytics-api/v2/venues/${venueId}/purchases/${purchaseId}`)
    .then(response => response.data);

export const fetchPurchaseCsvV2 = ({
  venueId,
  start_time,
  end_time,
  filters,
  locale,
}: {
  venueId: string;
  start_time: string;
  end_time: string;
  locale: string;
  filters?: AnalyticsCSVFilter[];
}) =>
  merchantGatewayApi
    .get<Blob>(`/merchant-analytics-api/v2/venues/${venueId}/purchases/csv`, {
      params: {
        end_date: end_time,
        start_date: start_time,
        filters,
      },
      paramsSerializer: params => queryString.stringify(params, { arrayFormat: 'comma' }),
      responseType: 'arraybuffer',
      headers: {
        'Accept-Language': locale,
      },
    })
    .then(response => response);

export const fetchPurchaseCSV = ({
  venueId,
  start_time,
  end_time,
  filters,
  locale,
}: {
  venueId: string;
  start_time: string;
  end_time: string;
  locale: string;
  filters?: AnalyticsFilter[];
}) =>
  merchantGatewayApi
    .get<Blob>(`/merchant-analytics-api/venues/${venueId}/purchases_history/csv`, {
      params: {
        end_time,
        start_time,
        filters,
      },
      paramsSerializer: params => queryString.stringify(params, { arrayFormat: 'comma' }),
      responseType: 'arraybuffer',
      headers: {
        'Accept-Language': locale,
      },
    })
    .then(response => response);

export const fetchItemsTotalsCsvV2 = ({
  venueId,
  start_time,
  end_time,
  filters,
  locale,
}: {
  venueId: string;
  start_time: string;
  end_time: string;
  locale: string;
  filters?: AnalyticsFilter[];
}) =>
  merchantGatewayApi
    .get<Blob>(`/merchant-analytics-api/v2/venues/${venueId}/items/totals/csv`, {
      params: { end_date: end_time, start_date: start_time, filters },
      paramsSerializer: params => queryString.stringify(params, { arrayFormat: 'comma' }),
      responseType: 'arraybuffer',
      headers: {
        'Accept-Language': locale,
      },
    })
    .then(response => response);

export const fetchItemsTotalsCSV = ({
  venueId,
  start_time,
  end_time,
  filters,
  locale,
}: {
  venueId: string;
  start_time: string;
  end_time: string;
  locale: string;
  filters?: AnalyticsFilter[];
}) =>
  merchantGatewayApi
    .get<Blob>(`/merchant-analytics-api/venues/${venueId}/items_totals/csv`, {
      params: {
        end_time,
        start_time,
        filters,
      },
      paramsSerializer: params => queryString.stringify(params, { arrayFormat: 'comma' }),
      responseType: 'arraybuffer',
      headers: {
        'Accept-Language': locale,
      },
    })
    .then(response => response);

export const fetchPurchaseItemsV2 = ({
  venueId,
  startDate,
  endDate,
  locale,
  filters,
  limit,
  cursor,
  sortField,
  sortOrder,
}: RestaurantApiCursorPaginationParams<{
  venueId: string;
  startDate: string;
  endDate: string;
  locale: string;
  filters?: AnalyticsFilter[];
  sortField: 'COUNT' | 'FINAL_PRICE_GROSS';
  sortOrder: 'ASC' | 'DESC';
}>): Promise<PaginatedData<PurchaseItem[]>> =>
  merchantGatewayApi
    .get<PurchaseItemsResponse>(`/merchant-analytics-api/v2/venues/${venueId}/items/totals`, {
      params: {
        end_date: endDate,
        start_date: startDate,
        filters,
        cursor,
        limit,
        sort_field: sortField,
        sort_order: sortOrder,
      },
      paramsSerializer: params => queryString.stringify(params, { arrayFormat: 'comma' }),
      headers: {
        'Accept-Language': locale,
      },
    })
    .then(response => ({
      data: response.data.data.totals,
      // Flatten the metadata to be able to use existing helpers for pagination
      metadata: response.data.metadata?.pagination,
    }));

export const fetchPurchaseItemOptionsV2 = ({
  venueId,
  itemId,
  startDate,
  endDate,
  locale,
  filters,
  limit,
  cursor,
  sortField,
  sortOrder,
}: RestaurantApiCursorPaginationParams<{
  venueId: string;
  itemId: string;
  startDate: string;
  endDate: string;
  locale: string;
  filters?: AnalyticsFilter[];
  sortField: 'COUNT' | 'FINAL_PRICE_GROSS';
  sortOrder: 'ASC' | 'DESC';
}>): Promise<PaginatedData<AggregatedPurchasedItemOptionValue[]>> =>
  merchantGatewayApi
    .get<AggregatedPurchasedItemOptionResponse>(
      `/merchant-analytics-api/v2/venues/${venueId}/items/${itemId}/options/totals`,
      {
        params: {
          end_date: endDate,
          start_date: startDate,
          filters,
          cursor,
          limit,
          sort_field: sortField,
          sort_order: sortOrder,
        },
        paramsSerializer: params => queryString.stringify(params, { arrayFormat: 'comma' }),
        headers: { 'Accept-Language': locale },
      },
    )
    .then(response => ({
      data: response.data.data.totals,
      // Flatten the metadata to be able to use existing helpers for pagination
      metadata: response.data.metadata?.pagination,
    }));

export const getPurchaseHistoryCSV = (venueIds: string[], interval: Interval, localLanguage: string) => {
  const data = { venueIds, startDate: interval.start.toISODate(), endDate: interval.end.toISODate() };
  return merchantGatewayApi
    .post<string>('/merchant-analytics-api/v2/venues/purchases/csv', data, {
      headers: { Accept: 'text/csv', 'Accept-Language': localLanguage },
    })
    .then(response => response.data);
};
